import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  NbActionsModule,
  NbContextMenuModule,
  NbIconModule,
  NbMediaBreakpointsService,
  NbMenuService,
  NbSidebarService,
  NbThemeService,
  NbTooltipModule,
  NbUserModule,
} from '@nebular/theme';
import { filter, map, takeUntil } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { LayoutService } from '../../layout.service';
import { NbMenuItem } from '@nebular/theme';
import { Router } from '@angular/router';
import { ThemeService } from '../../theme.service';
import { MainDataService } from '../../../services/main-data.service';
import { UserService } from '../../../auth/user.service';
import { BasicRole } from '@shared/userRoles';
import { HealthCheckNotificationDestinationType } from '@shared/models/health-check-notifications';
import { HealthCheckMessageComponent } from '../../../components/user-notifications/health-check-message/health-check-message.component';
import { UserNotificationsComponent } from '../../../components/user-notifications/user-notifications.component';
import { AsyncPipe } from '@angular/common';
import { RootEntitiesSwitcherComponent } from '../../../projects/root-entities-switcher/root-entities-switcher.component';
import { Auth, signOut, user as getUser } from '@angular/fire/auth';
import { LogoService } from '../../../projects/logo.service';

@Component({
  selector: 'ngx-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
  imports: [
    NbIconModule,
    RootEntitiesSwitcherComponent,
    NbActionsModule,
    NbTooltipModule,
    UserNotificationsComponent,
    HealthCheckMessageComponent,
    NbUserModule,
    NbContextMenuModule,
    AsyncPipe,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  public metaAppUrl: string;

  private destroy$: Subject<void> = new Subject<void>();
  public userPictureOnly = false;
  public user: {
    name: string;
    email: string;
    photoUrl: string;
  };
  public userMenu: NbMenuItem[] = [{ title: 'Log out' }];

  protected logo$ = this.logoService.logo$.pipe(takeUntil(this.destroy$));

  public isHealthCheckNotificationsVisible: boolean;

  constructor(
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private nbThemeService: NbThemeService,
    private layoutService: LayoutService,
    private breakpointService: NbMediaBreakpointsService,
    private afa: Auth,
    private nbMenuService: NbMenuService,
    private router: Router,
    private themeService: ThemeService,
    private mainDataService: MainDataService,
    private userService: UserService,
    private logoService: LogoService,
  ) {
    this.isHealthCheckNotificationsVisible =
      userService.hasRole(BasicRole.admin) ||
      userService.dbUser?.healthCheckNotificationType?.includes(
        HealthCheckNotificationDestinationType.PORTAL,
      ) ||
      false;
  }

  public ngOnInit() {
    const { xl } = this.breakpointService.getBreakpointsMap();

    this.nbMenuService
      .onItemClick()
      .pipe(filter((menu) => menu.tag === 'userMenu' && menu.item.title === 'Log out'))
      .subscribe(async () => {
        await signOut(this.afa);
        this.router.navigate(['auth', 'login']);
      });

    getUser(this.afa).subscribe((user) => {
      if (user) {
        this.user = {
          name: user.displayName || user.email,
          email: user.email,
          photoUrl: user.photoURL,
        };
      } else {
        this.user = null;
      }
    });

    this.nbThemeService
      .onMediaQueryChange()
      .pipe(
        map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
        takeUntil(this.destroy$),
      )
      .subscribe((isLessThanXl: boolean) => (this.userPictureOnly = isLessThanXl));

    combineLatest([this.mainDataService.projectSettings$, this.userService.dbUser$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([projectSettings]) => {
        this.metaAppUrl =
          this.userService.hasRole(BasicRole.admin) && projectSettings.metaAppUrl
            ? projectSettings.metaAppUrl
            : null;
      });
  }

  public ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  public navigateHome() {
    this.menuService.navigateHome();
    return false;
  }

  public get themeIcon(): string {
    return this.themeService.isLightThemeActive$.value ? 'moon-outline' : 'sun-outline';
  }

  public toggleThemeState(): void {
    this.themeService.toggleThemeState();
  }

  public openMetaApp(): boolean {
    window.open(this.metaAppUrl, '_blank');
    return false;
  }
}
